import React from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Tooltip,
    Alert,
    Typography,
    Flex,
    Space,
    Dropdown,
    Row,
    Col,
    Grid,
    Card,
} from 'antd';
import '@ant-design/v5-patch-for-react-19';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    LockOutlined,
    LogoutOutlined,
    ExportOutlined,
    ImportOutlined,
    SafetyOutlined,
    CopyOutlined,
    CheckOutlined,
    UndoOutlined,
    MoreOutlined,
    KeyOutlined,
    SearchOutlined,
    SettingOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons';
import {SyncSettings} from './SyncSettings';
import UpdateNotifier from './UpdateNotifier';
import selfPassLogo from './assets/selfpass-logo.png';
import usePasswordManager, {appVersion} from './hooks/usePasswordManager';

const {Paragraph} = Typography;
const {useBreakpoint} = Grid;

const PasswordManager = () => {
    const pm = usePasswordManager();
    const screens = useBreakpoint();
    const isMobile = typeof screens.md === 'boolean'
        ? !screens.md
        : (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);

    const {
        form,
        authForm,
        isModalVisible,
        setIsModalVisible,
        editingPassword,
        authVisible,
        vaultSession,
        copiedPasswordId,
        copiedLoginId,
        welcomeVisible,
        saltChoiceVisible,
        newSalt,
        setNewSalt,
        customSalt,
        setCustomSalt,
        step,
        setStep,
        searchTerm,
        setSearchTerm,
        isNewVault,
        isUnlocking,
        syncSettingsKey,
        isYandexSyncing,
        setIsYandexSyncing,
        accountInfo,
        activePage,
        setActivePage,
        lockVault,
        confirmExitVault,
        handleWelcomeContinue,
        handleSaltChoice,
        handleCustomSaltSubmit,
        handleNewSaltConfirm,
        generatePassword,
        copyToClipboard,
        handleAuth,
        handleExportEncrypted,
        handleExportForExtension,
        handleImport,
        handleAdd,
        handleEdit,
        handleDelete,
        handleRestore,
        handleSubmit,
        handleMenuClick,
        filteredPasswords,
        setPasswords,
    } = pm;

    const renderWelcomeModal = () => (
        <Modal
            title={null}
            open={welcomeVisible}
            footer={null}
            closable={false}
            centered
            width={isMobile ? '100%' : 520}
            styles={{header: {display: 'none'}}}
        >
            <div className="app-brand app-brand--auth">
                <img src={selfPassLogo} alt="SelfPass" className="app-logo" />
                <p className="app-brand-subtitle">Локальный менеджер паролей</p>
            </div>
            <p className="welcome-description">
                SelfPass хранит пароли в зашифрованном сейфе на вашем устройстве.
                Доступ защищён ключом шифрования и мастер-паролем — без них данные
                восстановить нельзя.
            </p>
            <Button type="primary" size="large" block onClick={handleWelcomeContinue}>
                Начать настройку
            </Button>
        </Modal>
    );

    const renderSaltChoiceModal = () => (
        <Modal
            title="Настройка безопасности"
            open={saltChoiceVisible}
            footer={null}
            closable={false}
            width={isMobile ? '100%' : 520}
            centered
        >
            {step === 1 && (
                <div style={{textAlign: 'center'}}>
                    <Alert
                        message="Важное предупреждение"
                        description="Ключ является критически важным элементом безопасности. Потеря ключа приведет к невозможности восстановления данных!"
                        type="warning"
                        showIcon
                        style={{marginBottom: 20}}
                    />
                    <div className="salt-choice-actions">
                        <Button
                            type="primary"
                            onClick={() => handleSaltChoice('generate')}
                        >
                            Сгенерировать новый ключ
                        </Button>
                        <Button
                            onClick={() => handleSaltChoice('custom')}
                        >
                            Использовать свой ключ
                        </Button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div>
                    <Alert
                        message="Сохраните ключ"
                        description="Эта строка должна быть сохранена в надежном месте. Без нее вы не сможете восстановить доступ!"
                        type="error"
                        showIcon
                    />
                    <div style={{
                        position: 'relative',
                        background: '#f5f5f5',
                        padding: '10px 32px 10px 10px',
                        borderRadius: 4,
                        margin: '16px 0',
                        wordBreak: 'break-all'
                    }}>
                        {newSalt}
                        <Paragraph
                            copyable={{text: newSalt, icon: [<CopyOutlined key="copy"/>]}}
                            style={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                                margin: 0,
                                lineHeight: 1
                            }}
                        />
                    </div>
                    <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <Button
                            type="primary"
                            onClick={handleNewSaltConfirm}
                        >
                            Я сохранил ключ, продолжить
                        </Button>
                        <Button
                            onClick={() => {
                                setNewSalt('');
                                setStep(1);
                            }}
                        >
                            Отмена
                        </Button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <Form layout="vertical">
                    <Form.Item
                        label="Введите ваш ключ"
                        rules={[
                            {required: true, message: 'Введите ключ'},
                            {min: 16, message: 'Минимум 16 символов'}
                        ]}
                    >
                        <Input.Password
                            value={customSalt}
                            onChange={(e) => setCustomSalt(e.target.value)}
                            placeholder="Минимум 16 символов"
                        />
                    </Form.Item>
                    <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <Button
                            type="primary"
                            onClick={handleCustomSaltSubmit}
                        >
                            Подтвердить ключ
                        </Button>
                        <Button onClick={() => setStep(1)}>
                            Назад
                        </Button>
                    </div>
                </Form>
            )}
        </Modal>
    );

    const renderAuthModal = () => (
        <Modal
            title={null}
            open={authVisible && !welcomeVisible && !saltChoiceVisible}
            footer={null}
            closable={false}
            centered
            width={isMobile ? '100%' : 520}
            styles={{header: {display: 'none'}}}
        >
            <div className="app-brand app-brand--auth">
                <img src={selfPassLogo} alt="SelfPass" className="app-logo" />
                <p className="app-brand-subtitle">
                    {isNewVault ? 'Создайте мастер-пароль' : 'Введите мастер-пароль'}
                </p>
            </div>
            {isNewVault && (
                <Alert
                    message="Новый сейф"
                    description="Подтвердите мастер-пароль. Он не хранится на сервере и не может быть восстановлен."
                    type="info"
                    showIcon
                    style={{marginBottom: 16}}
                />
            )}
            <Form form={authForm} onFinish={handleAuth}>
                <Form.Item
                    name="password"
                    rules={[
                        {required: true, message: 'Введите мастер-пароль'},
                        ...(isNewVault ? [{min: 8, message: 'Минимум 8 символов'}] : []),
                    ]}
                >
                    <Input.Password
                        prefix={<SafetyOutlined/>}
                        placeholder="Мастер-пароль"
                        autoComplete="current-password"
                    />
                </Form.Item>
                {isNewVault && (
                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                            {required: true, message: 'Подтвердите мастер-пароль'},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Пароли не совпадают'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<SafetyOutlined/>}
                            placeholder="Повторите мастер-пароль"
                            autoComplete="new-password"
                        />
                    </Form.Item>
                )}
                <Space wrap style={{width: '100%'}} className={isMobile ? 'salt-choice-actions' : undefined}>
                    <Button type="primary" htmlType="submit" loading={isUnlocking} block={isMobile}>
                        {isNewVault ? 'Создать сейф' : 'Войти'}
                    </Button>
                    {!isNewVault && (
                        <Button
                            danger
                            icon={<LogoutOutlined/>}
                            onClick={confirmExitVault}
                            disabled={isUnlocking}
                            block={isMobile}
                        >
                            Другое хранилище
                        </Button>
                    )}
                </Space>
            </Form>
        </Modal>
    );

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
        },
        {
            title: 'Логин',
            dataIndex: 'username',
            key: 'username',
            render: (text, record) => (
                <div className="username-cell">
                    <Input
                        variant="borderless"
                        value={text || ''}
                        readOnly
                        className="responsive-input"
                        style={{cursor: 'default'}}
                        addonAfter={
                            text ? (
                                <Tooltip title="Копировать логин">
                                    <Button
                                        icon={copiedLoginId === record.key ? <CheckOutlined/> : <CopyOutlined/>}
                                        onClick={() => copyToClipboard(text, record.key, 'login')}
                                        size="small"
                                        type="text"
                                        style={{color: '#1890ff'}}
                                    />
                                </Tooltip>
                            ) : null
                        }
                    />
                </div>
            ),
        },
        {
            title: 'Пароль',
            dataIndex: 'password',
            key: 'password',
            render: (text, record) => (
                <div className="password-cell">
                    <Input
                        variant="borderless"
                        value="••••••••••••••••"
                        readOnly
                        className="responsive-input"
                        style={{cursor: 'default'}}
                        addonAfter={
                            <Tooltip title="Копировать в буфер">
                                <Button
                                    icon={copiedPasswordId === record.key ? <CheckOutlined/> : <CopyOutlined/>}
                                    onClick={() => copyToClipboard(text, record.key, 'password')}
                                    size="small"
                                    type="text"
                                    style={{color: '#1890ff'}}
                                />
                            </Tooltip>
                        }
                    />
                </div>
            ),
        },
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            render: (text) => text ? (
                <a
                    href={text}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="url-cell"
                    title={text}
                >
                    {text}
                </a>
            ) : null,
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <div className="actions-container">
                    {record.deletedAt ? (
                        <Button
                            icon={<UndoOutlined/>}
                            onClick={() => handleRestore(record.key)}
                            style={{color: '#52c41a'}}
                        />
                    ) : (
                        <>
                            <Button
                                icon={<EditOutlined/>}
                                onClick={() => handleEdit(record)}
                                style={{marginRight: 8}}
                            />
                            <Button
                                icon={<DeleteOutlined/>}
                                onClick={() => handleDelete(record.key)}
                                danger
                            />
                        </>
                    )}
                </div>
            ),
        },
    ];

    const renderPasswordCards = () => {
        if (filteredPasswords.length === 0) {
            return (
                <div className="password-card__empty">
                    {searchTerm ? 'Ничего не найдено' : 'Пока нет сохранённых паролей'}
                </div>
            );
        }

        return (
            <div className="password-cards">
                {filteredPasswords.map((record) => (
                    <div className="password-card" key={record.key}>
                        <div className="password-card__header">
                            <h3 className="password-card__title">{record.name}</h3>
                            <div className="password-card__actions">
                                {record.deletedAt ? (
                                    <Button
                                        icon={<UndoOutlined/>}
                                        onClick={() => handleRestore(record.key)}
                                        type="text"
                                        style={{color: '#52c41a'}}
                                    />
                                ) : (
                                    <>
                                        <Button
                                            icon={<EditOutlined/>}
                                            onClick={() => handleEdit(record)}
                                            type="text"
                                        />
                                        <Button
                                            icon={<DeleteOutlined/>}
                                            onClick={() => handleDelete(record.key)}
                                            type="text"
                                            danger
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="password-card__row">
                            <span className="password-card__label">Логин</span>
                            <span className="password-card__value" title={record.username || ''}>
                                {record.username || '—'}
                            </span>
                            {record.username ? (
                                <Button
                                    icon={copiedLoginId === record.key ? <CheckOutlined/> : <CopyOutlined/>}
                                    onClick={() => copyToClipboard(record.username, record.key, 'login')}
                                    type="text"
                                    style={{color: '#1890ff'}}
                                />
                            ) : null}
                        </div>

                        <div className="password-card__row">
                            <span className="password-card__label">Пароль</span>
                            <span className="password-card__value">••••••••••••••••</span>
                            <Button
                                icon={copiedPasswordId === record.key ? <CheckOutlined/> : <CopyOutlined/>}
                                onClick={() => copyToClipboard(record.password, record.key, 'password')}
                                type="text"
                                style={{color: '#1890ff'}}
                            />
                        </div>

                        {record.url ? (
                            <div className="password-card__row">
                                <span className="password-card__label">URL</span>
                                <a
                                    href={record.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="password-card__value password-card__url"
                                    title={record.url}
                                >
                                    {record.url}
                                </a>
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
        );
    };

    const actionsMenu = {
        items: [
            {
                key: 'settings',
                icon: <SettingOutlined/>,
                label: 'Настройки',
                disabled: authVisible || !vaultSession,
            },
            {
                key: 'lock',
                icon: <LockOutlined/>,
                label: 'Заблокировать',
                disabled: authVisible || !vaultSession,
            },
            {
                key: 'exit',
                icon: <LogoutOutlined/>,
                label: 'Выйти из хранилища',
                danger: true,
                disabled: (authVisible && isNewVault) || isYandexSyncing,
            },
        ],
        onClick: handleMenuClick,
    };

    return (
        <>
            {renderWelcomeModal()}
            {renderSaltChoiceModal()}
            {renderAuthModal()}
            <div className="app-shell">
                <div className="version-info">
                    <span>Версия: {appVersion}</span>
                    <UpdateNotifier />
                </div>

                <header className="app-brand app-brand--header">
                    <img src={selfPassLogo} alt="SelfPass" className="app-logo" />
                </header>

                <Flex gap={16} wrap="wrap" vertical={true}>
                    {activePage === 'settings' ? (
                        <>
                            <Space>
                                <Button
                                    icon={<ArrowLeftOutlined/>}
                                    onClick={() => setActivePage('vault')}
                                >
                                    Назад
                                </Button>
                            </Space>

                            <Card title="Аккаунт" size="small">
                                {accountInfo ? (
                                    <div className="settings-account">
                                        <div>
                                            ID: <Typography.Text copyable={{text: accountInfo.accountId}}>
                                                {accountInfo.accountId.slice(0, 8)}…{accountInfo.accountId.slice(-4)}
                                            </Typography.Text>
                                        </div>
                                        <div>
                                            Статус:{' '}
                                            {accountInfo.accountStatus === 'confirmed'
                                                ? 'подтверждён'
                                                : 'не подтверждён'}
                                        </div>
                                        <div>
                                            Сейф: {accountInfo.vaultName || 'Основной сейф'}
                                        </div>
                                    </div>
                                ) : (
                                    <span>Аккаунт ещё не создан</span>
                                )}
                            </Card>

                            <Card title="Синхронизация" size="small">
                                <SyncSettings
                                    key={syncSettingsKey}
                                    variant="full"
                                    vaultSession={vaultSession}
                                    onSyncingChange={setIsYandexSyncing}
                                    onSync={(updated) => {
                                        setPasswords(updated);
                                    }}
                                />
                            </Card>

                            <Card title="Импорт и экспорт" size="small">
                                <div className="settings-actions">
                                    <Button
                                        icon={<ExportOutlined/>}
                                        onClick={handleExportEncrypted}
                                        disabled={authVisible || !vaultSession}
                                    >
                                        Экспорт (зашифрованный)
                                    </Button>
                                    <Button
                                        icon={<KeyOutlined/>}
                                        onClick={handleExportForExtension}
                                        disabled={authVisible || !vaultSession}
                                    >
                                        Экспорт для расширения
                                    </Button>
                                    <Button
                                        icon={<ImportOutlined/>}
                                        onClick={() => document.getElementById('import-input').click()}
                                        disabled={authVisible || !vaultSession}
                                    >
                                        Импорт
                                    </Button>
                                    <input
                                        id="import-input"
                                        type="file"
                                        accept=".json"
                                        style={{display: 'none'}}
                                        onChange={(e) => {
                                            if (e.target.files[0]) {
                                                handleImport(e.target.files[0]);
                                            }
                                            e.target.value = null;
                                        }}
                                    />
                                </div>
                            </Card>
                        </>
                    ) : (
                        <>
                            <SyncSettings
                                key={syncSettingsKey}
                                variant="status"
                                vaultSession={vaultSession}
                                onSyncingChange={setIsYandexSyncing}
                                onSync={(updated) => {
                                    setPasswords(updated);
                                }}
                            />

                            <div className="toolbar">
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined/>}
                                    onClick={handleAdd}
                                    className="mobile-full-width"
                                    size={isMobile ? 'large' : 'middle'}
                                    disabled={authVisible || !vaultSession || isYandexSyncing}
                                >
                                    Добавить
                                </Button>

                                <div className="toolbar-actions desktop-only">
                                    <Button
                                        icon={<SettingOutlined/>}
                                        onClick={() => setActivePage('settings')}
                                        disabled={authVisible || !vaultSession}
                                    >
                                        Настройки
                                    </Button>

                                    <Button
                                        icon={<LockOutlined/>}
                                        onClick={lockVault}
                                        disabled={authVisible || !vaultSession}
                                    >
                                        Блокировка
                                    </Button>

                                    <Button
                                        danger
                                        icon={<LogoutOutlined/>}
                                        onClick={confirmExitVault}
                                        disabled={(authVisible && isNewVault) || isYandexSyncing}
                                    >
                                        Выйти
                                    </Button>
                                </div>

                                <Dropdown
                                    menu={actionsMenu}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <Button
                                        icon={<MoreOutlined/>}
                                        className="mobile-only"
                                        size="large"
                                        aria-label="Меню"
                                    />
                                </Dropdown>
                            </div>

                            <Input
                                placeholder="Поиск по названию..."
                                prefix={<SearchOutlined />}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                size="large"
                                allowClear
                            />

                            {isMobile ? (
                                renderPasswordCards()
                            ) : (
                                <Table
                                    columns={columns}
                                    dataSource={filteredPasswords}
                                    rowKey="key"
                                    pagination={false}
                                    className="responsive-table"
                                    scroll={{x: true}}
                                />
                            )}
                        </>
                    )}
                </Flex>

                <Modal
                    title={`${editingPassword ? 'Редактировать' : 'Новый'} пароль`}
                    open={isModalVisible}
                    onOk={handleSubmit}
                    onCancel={() => setIsModalVisible(false)}
                    okText={editingPassword ? 'Обновить' : 'Добавить'}
                    width={isMobile ? '100%' : 600}
                    centered={isMobile}
                    cancelText="Отмена"
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="name"
                            label="Название"
                            rules={[{required: true, message: 'Введите название'}]}
                        >
                            <Input size="large"/>
                        </Form.Item>

                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="username"
                                    label="Логин"
                                    rules={[{required: true}]}
                                >
                                    <Input size="large" autoComplete="username"/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="password"
                                    label="Пароль"
                                    rules={[{required: true}]}
                                >
                                    <Input.Password
                                        size="large"
                                        autoComplete="new-password"
                                        addonAfter={
                                            <Button
                                                type="text"
                                                onClick={generatePassword}
                                                icon={<KeyOutlined/>}
                                            />
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="url"
                            label="URL"
                            rules={[{type: 'url', message: 'Введите корректный URL'}]}
                        >
                            <Input size="large"/>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default PasswordManager;
